<template>
  <div class="media-viewer">
    <div v-if="loading" class="media-loader">
      <div class="spinner"></div>
      <p>Loading media...</p>
    </div>
    <div v-else-if="error" class="media-error">
      <p>{{ error }}</p>
      <button class="retry-button"
              @click="retry"
      >
        Retry
      </button>
    </div>
    <div v-else class="media-content relative">
      <!-- Image display -->
      <div v-if="isImage" :style="{ width: imageWidth, height: 'auto', overflow: 'hidden' }">
        <img :src="mediaUrl"
             :style="{transform: `scale(${zoomLevel})`}"
             class="media-image"
             @load="onMediaLoaded"
             @error="onMediaError"
        />
        <div class="absolute top-2 right-2 flex gap-2">
          <button
            class="bg-primary-500 text-white px-2 py-1 rounded-md text-sm opacity-80 hover:opacity-100"
            @click="downloadFile"
          >Download</button>
          <button
            v-if="canDelete"
            class="bg-red-500 text-white px-2 py-1 rounded-md text-sm opacity-80 hover:opacity-100"
            :disabled="deleting"
            @click="deleteMedia"
          >{{ deleting ? 'Deleting...' : 'Delete' }}</button>
        </div>
      </div>

      <!-- Non-image file display -->
      <div v-else class="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div class="shrink-0 w-8 h-8 flex items-center justify-center bg-gray-200 rounded text-gray-500 text-xs font-bold">
          {{ fileExtension }}
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900 truncate">{{ displayFilename }}</p>
        </div>
        <div class="flex gap-2">
          <button
            class="bg-primary-500 text-white px-2 py-1 rounded-md text-sm hover:bg-primary-600"
            @click="downloadFile"
          >Download</button>
          <button
            v-if="canDelete"
            class="bg-red-500 text-white px-2 py-1 rounded-md text-sm hover:bg-red-600"
            :disabled="deleting"
            @click="deleteMedia"
          >{{ deleting ? 'Deleting...' : 'Delete' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {mapGetters} from 'vuex';

export default {
  name: 'MediaViewer',
  props: {
    canDelete: {
      type: Boolean,
      default: false
    },
    imageWidth: {
      type: String,
      default: '200px'
    },
    mediaKey: {
      type: String,
      required: true
    },
    uploadedBy: {
      type: Object,
      default: null
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      mediaUrl: '',
      mediaType: null,
      zoomLevel: 1,
      mediaMetadata: null,
      deleting: false
    }
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    isImage() {
      return this.mediaType && this.mediaType.startsWith('image/')
    },
    fileExtension() {
      const ext = this.mediaKey.split('.').pop().toUpperCase();
      return ext.length <= 4 ? ext : 'FILE';
    },
    displayFilename() {
      if (this.mediaMetadata?.filename) return this.mediaMetadata.filename;
      const parts = this.mediaKey.split('/');
      return parts[parts.length - 1] || 'Unknown file';
    },
  },
  methods: {
    async loadMedia() {
      if (!this.mediaKey) {
        this.error = 'No media key provided'
        this.loading = false
        return
      }

      this.loading = true
      this.error = null

      // Determine type from key extension first
      this.mediaType = this.determineTypeFromKey();

      // For non-images, don't fetch the full blob — just show file info
      if (!this.mediaType.startsWith('image/')) {
        this.loading = false;
        return;
      }

      try {
        const response = await axios.get('api/media/', {
          params: { key: this.mediaKey },
          responseType: 'blob'
        });

        this.mediaType = response.headers['content-type'] ||
                          response.data.type ||
                          this.mediaType;

        const blob = new Blob([response.data], { type: this.mediaType });
        this.mediaUrl = URL.createObjectURL(blob);

        if (response.headers['media-metadata']) {
          try {
            this.mediaMetadata = JSON.parse(response.headers['media-metadata']);
          } catch (e) {
            console.warn('Could not parse media metadata:', e);
          }
        }
      } catch (err) {
        console.error('Error loading media:', err)
        this.error = 'Failed to load media'
      } finally {
        this.loading = false
      }
    },
    hideBlockedPreview() {
      if (this.mediaUrl) URL.revokeObjectURL(this.mediaUrl);
      this.mediaUrl = '';
      this.error = 'Previews are hidden while Demo Mode is on.';
      this.loading = false;
    },
    determineTypeFromKey() {
      const extension = this.mediaKey.split('.').pop().toLowerCase();
      const mimeMap = {
        jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp',
        pdf: 'application/pdf',
        doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ppt: 'application/vnd.ms-powerpoint', pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      };
      return mimeMap[extension] || 'application/octet-stream';
    },
    retry() {
      this.loadMedia()
    },
    onMediaLoaded() {
      this.loading = false
    },
    onMediaError() {
      this.loading = false
      this.error = 'Failed to load media'
    },
    async deleteMedia() {
      if (!this.canDelete) {
        return;
      }

      this.deleting = true;

      try {
        const response = await axios.delete('api/media/', {
          params: { key: this.mediaKey }
        });

        if (response.data.success) {
          // Emit event to notify parent that media was deleted
          this.$emit('media-deleted', this.mediaKey);
        } else {
          this.error = response.data.error || 'Failed to delete media';
        }
      } catch (err) {
        console.error('Error deleting media:', err);
        this.error = err.response?.data?.error || 'Failed to delete media';
      } finally {
        this.deleting = false;
      }
    },
    async downloadFile() {
      try {
        let blobUrl = this.mediaUrl;
        // For non-images, mediaUrl may not be set (blob wasn't loaded for display)
        if (!blobUrl) {
          const response = await axios.get('api/media/', {
            params: { key: this.mediaKey },
            responseType: 'blob'
          });
          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
          blobUrl = URL.createObjectURL(blob);
        }

        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', this.displayFilename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up temporary blob URL (not the one used for image display)
        if (blobUrl !== this.mediaUrl) {
          URL.revokeObjectURL(blobUrl);
        }
      } catch (err) {
        console.error('Error downloading file:', err);
        this.error = 'Failed to download file';
      }
    },
  },
  watch: {
    mediaKey() {
      this.loadMedia()
    },
  },
  mounted() {
    this.loadMedia()
  },
  beforeUnmount() {
    // Clean up any blob URLs to prevent memory leaks
    if (this.mediaUrl && this.mediaUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.mediaUrl);
    }
  }
}
</script>

<style scoped>
</style>
